import {
  FC,
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';

import MenuIcon from 'assets/icons/menu.svg';
import MicIcon from 'assets/icons/mic.svg';
import MicOffIcon from 'assets/icons/mic-off.svg';
import AssistantIcon from 'assets/icons/nav/support.svg';
import WorldIcon from 'assets/icons/nav/world.svg';
import { store } from 'store';
import {
  getMicAnalyser,
  isConnected,
  isConnecting,
  setMicMuted,
  startVoice,
  stopVoice,
} from 'store/assistant/voiceManager';
import { Mode } from 'types';

import { MENU_ITEMS } from './consts';
import * as S from './Menu.styles';

const communityItem = MENU_ITEMS.find((item) => item.id === 'community');
const mobileMoreItems = ['community', 'vision']
  .map((id) => MENU_ITEMS.find((item) => item.id === id))
  .filter((item): item is (typeof MENU_ITEMS)[number] => Boolean(item));

const NAV_ITEMS = [
  { id: 'main', label: 'Galaxy', icon: <WorldIcon /> },
  ...MENU_ITEMS.filter(
    (item) =>
      item.id !== 'community' &&
      item.id !== 'sophie' &&
      item.id !== 'chains' &&
      item.id !== 'nft'
  ),
];

const ACTIVE_NAV_BY_MODE: Partial<Record<Mode, string>> = {
  main: 'main',
  swap: 'swap',
  pilot: 'copy',
  autopilot: 'copy',
  cockpit: 'portfolio',
  sale: 'nft',
  whitepaper: 'vision',
};

const MoreGridIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4.5 4.5h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.5 4.5h4a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 15.5h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15.5 15.5h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-4a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const Menu: FC = observer(() => {
  const { mode, isMobile, toggleMode } = store.commonStore;
  const { requestPortalFlight } = store.galaxyStore;
  const { voiceState, sophiePhase, isMicMuted, setAssistantMode, setVoiceState } =
    store.assistantStore;

  const [communityOpen, setCommunityOpen] = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(false);
  const communityRef = useRef<HTMLDivElement>(null);
  const centerNavRef = useRef<HTMLDivElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const assistantBtnRef = useRef<HTMLButtonElement>(null);
  const eqCanvasRef = useRef<HTMLCanvasElement>(null);
  const micAnimRef = useRef<number | null>(null);

  const activeNavId = ACTIVE_NAV_BY_MODE[mode] ?? null;
  const isVoiceActive = voiceState === 'active';
  const isVoiceLoading = voiceState === 'connecting';
  const isListening = isVoiceActive && sophiePhase === 'listening';
  const isThinking = isVoiceActive && sophiePhase === 'thinking';
  const isSpeaking = isVoiceActive && sophiePhase === 'speaking';
  const currentPhase: 'listening' | 'thinking' | 'speaking' | null = isListening
    ? 'listening'
    : isThinking
      ? 'thinking'
      : isSpeaking
        ? 'speaking'
        : null;
  const voiceStatus = isListening
    ? 'is listening'
    : isThinking
      ? 'is thinking'
      : isSpeaking
        ? 'is speaking'
        : isVoiceLoading
          ? 'Connecting'
          : 'Voice assistant';

  const handleItemClick = useCallback(
    (id: string) => {
      setCommunityOpen(false);
      setMobileMoreOpen(false);

      switch (id) {
        case 'main':
          toggleMode('main');
          break;
        case 'swap':
          toggleMode('swap');
          break;
        case 'copy':
          toggleMode('pilot');
          break;
        case 'portfolio':
          toggleMode('cockpit');
          break;
        case 'nft':
          toggleMode('sale');
          break;
        case 'vision':
          toggleMode('whitepaper');
          break;
        case 'chains':
          requestPortalFlight();
          break;
      }
    },
    [requestPortalFlight, toggleMode]
  );

  useEffect(() => {
    if (isMobile || !communityOpen) return;

    const handleClickOutside = (event: globalThis.MouseEvent) => {
      if (!communityRef.current?.contains(event.target as Node)) {
        setCommunityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [communityOpen, isMobile]);

  useEffect(() => {
    if (!isMobile || (!mobileMoreOpen && !communityOpen)) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (!mobileMenuRef.current?.contains(event.target as Node)) {
        setMobileMoreOpen(false);
        setCommunityOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [communityOpen, isMobile, mobileMoreOpen]);

  const navItems = useMemo(() => NAV_ITEMS, []);

  useEffect(() => {
    const nav = centerNavRef.current;
    if (!nav) return;

    const updateScrollHint = () => {
      const remainingScroll = nav.scrollWidth - nav.clientWidth - nav.scrollLeft;
      setShowScrollHint(remainingScroll > 8);
    };

    updateScrollHint();
    nav.addEventListener('scroll', updateScrollHint, { passive: true });
    window.addEventListener('resize', updateScrollHint);

    return () => {
      nav.removeEventListener('scroll', updateScrollHint);
      window.removeEventListener('resize', updateScrollHint);
    };
  }, [navItems.length, isMobile]);

  // ── EQ bars on top edge of button when listening ───────────────────────
  useEffect(() => {
    const btn = assistantBtnRef.current;
    const canvas = eqCanvasRef.current;

    const clear = () => {
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };

    if (!isListening || isMicMuted) {
      if (micAnimRef.current) {
        cancelAnimationFrame(micAnimRef.current);
        micAnimRef.current = null;
      }
      clear();
      return;
    }

    const BAR_COUNT = 32;
    const smoothBands = new Float32Array(BAR_COUNT);

    const animate = () => {
      micAnimRef.current = requestAnimationFrame(animate);
      const analyser = getMicAnalyser();
      if (!analyser || !btn || !canvas) return;

      const freqData = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(freqData);

      // Smooth each band
      for (let i = 0; i < BAR_COUNT; i++) {
        const fi = Math.floor((i / BAR_COUNT) * freqData.length * 0.45);
        const v0 = freqData[Math.max(0, fi - 1)] / 255;
        const v1 = freqData[fi] / 255;
        const v2 = freqData[Math.min(freqData.length - 1, fi + 1)] / 255;
        const raw = (v0 + v1 * 2 + v2) / 4;
        smoothBands[i] =
          raw > smoothBands[i] ? raw * 0.55 + smoothBands[i] * 0.45 : raw * 0.1 + smoothBands[i] * 0.9;
      }

      // Size canvas: sits above the button, only on flat part (skip rounded corners)
      const rect = btn.getBoundingClientRect();
      const borderR = 16; // button border-radius
      const maxBarH = 20; // half the previous height
      const dpr = window.devicePixelRatio || 1;
      const flatW = rect.width - borderR * 2; // flat portion between rounded corners
      const ch = maxBarH + 6; // extra for glow bleed
      const pw = Math.round(flatW * dpr);
      const ph = Math.round(ch * dpr);

      if (canvas.width !== pw || canvas.height !== ph) {
        canvas.width = pw;
        canvas.height = ph;
        canvas.style.width = `${flatW}px`;
        canvas.style.height = `${ch}px`;
        canvas.style.left = `${borderR}px`;
        canvas.style.top = `${-ch}px`;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, flatW, ch);

      // Draw bars from bottom of canvas (= top edge of button) going up
      const gap = 2;
      const barW = (flatW - gap * (BAR_COUNT + 1)) / BAR_COUNT;
      const baseY = ch; // bottom of canvas = top of button

      for (let i = 0; i < BAR_COUNT; i++) {
        const val = smoothBands[i];
        const barH = Math.max(0, val * maxBarH);
        if (barH < 0.5) continue;

        const x = gap + i * (barW + gap);
        const y = baseY - barH;
        const alpha = 0.5 + val * 0.5;

        // Glow layer
        ctx.shadowBlur = 6 + val * 10;
        ctx.shadowColor = `rgba(0, 255, 200, ${0.3 + val * 0.5})`;
        ctx.fillStyle = `rgba(0, 255, 200, ${alpha * 0.7})`;
        ctx.beginPath();
        ctx.roundRect(x, y, barW, barH, 1.5);
        ctx.fill();

        // Bright core
        ctx.shadowBlur = 0;
        ctx.fillStyle = `rgba(0, 255, 200, ${alpha})`;
        ctx.beginPath();
        ctx.roundRect(x + 0.5, y + 0.5, barW - 1, barH - 1, 1);
        ctx.fill();
      }
    };

    animate();

    return () => {
      if (micAnimRef.current) {
        cancelAnimationFrame(micAnimRef.current);
        micAnimRef.current = null;
      }
      clear();
    };
  }, [isListening, isMicMuted]);

  const handleVoiceClick = useCallback(async () => {
    if (isConnecting()) return;

    if (isConnected()) {
      stopVoice();
      setAssistantMode(null);
      return;
    }

    setAssistantMode('voice');
    setVoiceState('connecting');
    await startVoice();
  }, [setAssistantMode, setVoiceState]);

  const handleMicToggle = useCallback(
    (event: ReactMouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setMicMuted(!isMicMuted);
    },
    [isMicMuted]
  );

  const mobilePrimaryItems = useMemo(
    () => [
      { id: 'main', label: 'Galaxy', icon: <WorldIcon /> },
      ...MENU_ITEMS.filter((item) => ['swap', 'copy', 'portfolio'].includes(item.id)),
    ],
    []
  );

  return (
    <S.Root>
      <S.BottomNav>
        {isMobile ? (
          <S.MobileDock ref={mobileMenuRef}>
            <S.MobileMenuPanel $open={mobileMoreOpen}>
              {communityItem && (
                <S.MobileCommunityTray $open={communityOpen}>
                  {communityItem.subItems?.map((subItem) => (
                    <S.MobileCommunityIconLink
                      key={subItem.id}
                      href={subItem.href}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={subItem.label}
                      title={subItem.label}
                      onClick={() => {
                        setCommunityOpen(false);
                        setMobileMoreOpen(false);
                      }}
                    >
                      <S.CommunityIcon>{subItem.icon}</S.CommunityIcon>
                    </S.MobileCommunityIconLink>
                  ))}
                </S.MobileCommunityTray>
              )}

              {mobileMoreItems.map((item) => (
                <S.MobileMenuItem
                  key={item.id}
                  $active={item.id === 'community' ? communityOpen : activeNavId === item.id}
                  onClick={() => {
                    if (item.id === 'community') {
                      setCommunityOpen((current) => !current);
                      return;
                    }

                    handleItemClick(item.id);
                  }}
                >
                  <S.NavIcon $active={item.id === 'community' ? communityOpen : activeNavId === item.id}>
                    {item.icon}
                  </S.NavIcon>
                  <S.MobileMenuItemLabel
                    $active={item.id === 'community' ? communityOpen : activeNavId === item.id}
                  >
                    {item.label}
                  </S.MobileMenuItemLabel>
                </S.MobileMenuItem>
              ))}
            </S.MobileMenuPanel>

            <S.NavItems $isMobile={isMobile}>
              <S.AssistantSlot>
                <S.AssistantButton
                  ref={assistantBtnRef}
                  $active={isVoiceActive || isVoiceLoading}
                  $phase={currentPhase}
                  onClick={() => void handleVoiceClick()}
                  aria-pressed={isVoiceActive || isVoiceLoading}
                >
                  <S.EqCanvas ref={eqCanvasRef} aria-hidden />
                  <S.AssistantPhaseGlow
                    $visible={!!currentPhase}
                    $phase={currentPhase || 'listening'}
                    aria-hidden={!currentPhase}
                  />
                  <S.AssistantMain>
                    <S.AssistantIconWrap $active={isVoiceActive || isVoiceLoading} $phase={currentPhase}>
                      <AssistantIcon />
                    </S.AssistantIconWrap>
                    <S.AssistantText>
                      <S.AssistantTitle $active={isVoiceActive || isVoiceLoading}>
                        Sophie
                      </S.AssistantTitle>
                      <S.AssistantStatus $phase={currentPhase}>
                        {voiceStatus}
                      </S.AssistantStatus>
                    </S.AssistantText>
                  </S.AssistantMain>
                  <S.ThinkingDots $active={isThinking} aria-hidden={!isThinking}>
                    <span />
                    <span />
                    <span />
                  </S.ThinkingDots>
                  {isVoiceActive && (
                    <S.MicButton $pulse={isListening && isMicMuted} onClick={handleMicToggle} aria-label="Toggle microphone mute">
                      {isMicMuted ? <MicOffIcon /> : <MicIcon />}
                    </S.MicButton>
                  )}
                </S.AssistantButton>
              </S.AssistantSlot>

              <S.CenterNavWrap>
                <S.CenterNav ref={centerNavRef}>
                  {mobilePrimaryItems.map((item) => (
                    <S.NavButton
                      key={item.id}
                      $active={activeNavId === item.id}
                      onClick={() => handleItemClick(item.id)}
                      aria-pressed={activeNavId === item.id}
                    >
                      <S.NavIcon $active={activeNavId === item.id}>{item.icon}</S.NavIcon>
                      <S.NavLabel $active={activeNavId === item.id}>{item.label}</S.NavLabel>
                    </S.NavButton>
                  ))}

                  <S.NavButton
                    $active={mobileMoreOpen}
                    onClick={() => {
                      setCommunityOpen(false);
                      setMobileMoreOpen((current) => !current);
                    }}
                    aria-expanded={mobileMoreOpen}
                  >
                    <S.NavIcon $active={mobileMoreOpen}>
                      <MoreGridIcon />
                    </S.NavIcon>
                    <S.NavLabel $active={mobileMoreOpen}>Menu</S.NavLabel>
                  </S.NavButton>
                </S.CenterNav>
                <S.ScrollHint $visible={showScrollHint} aria-hidden={!showScrollHint}>
                  <span />
                </S.ScrollHint>
              </S.CenterNavWrap>

              <S.RightSpacer />
            </S.NavItems>
          </S.MobileDock>
        ) : (
          <S.NavItems $isMobile={isMobile}>
            <S.AssistantSlot>
              <S.AssistantButton
                ref={assistantBtnRef}
                $active={isVoiceActive || isVoiceLoading}
                $phase={currentPhase}
                onClick={() => void handleVoiceClick()}
                aria-pressed={isVoiceActive || isVoiceLoading}
              >
                <S.EqCanvas ref={eqCanvasRef} aria-hidden />
                <S.AssistantPhaseGlow
                  $visible={!!currentPhase}
                  $phase={currentPhase || 'listening'}
                  aria-hidden={!currentPhase}
                />
                <S.AssistantMain>
                  <S.AssistantIconWrap $active={isVoiceActive || isVoiceLoading} $phase={currentPhase}>
                    <AssistantIcon />
                  </S.AssistantIconWrap>
                  <S.AssistantText>
                    <S.AssistantTitle $active={isVoiceActive || isVoiceLoading}>
                      Sophie
                    </S.AssistantTitle>
                    <S.AssistantStatus $phase={currentPhase}>
                      {voiceStatus}
                    </S.AssistantStatus>
                  </S.AssistantText>
                </S.AssistantMain>
                <S.RightSlot>
                  <S.ThinkingDots $active={isThinking} aria-hidden={!isThinking}>
                    <span />
                    <span />
                    <span />
                  </S.ThinkingDots>
                  <S.MicButton
                    $pulse={isListening && isMicMuted}
                    $hidden={!isVoiceActive || isThinking}
                    onClick={handleMicToggle}
                    aria-label="Toggle microphone mute"
                  >
                    {isMicMuted ? <MicOffIcon /> : <MicIcon />}
                  </S.MicButton>
                </S.RightSlot>
              </S.AssistantButton>
            </S.AssistantSlot>

            <S.CenterNavWrap>
              <S.CenterNav ref={centerNavRef}>
                {navItems.map((item) => (
                  <S.NavButton
                    key={item.id}
                    $active={activeNavId === item.id}
                    onClick={() => handleItemClick(item.id)}
                    aria-pressed={activeNavId === item.id}
                  >
                    <S.NavIcon $active={activeNavId === item.id}>{item.icon}</S.NavIcon>
                    <S.NavLabel $active={activeNavId === item.id}>{item.label}</S.NavLabel>
                  </S.NavButton>
                ))}

                {communityItem && (
                  <S.CommunityWrap
                    ref={communityRef}
                    onMouseEnter={() => !isMobile && setCommunityOpen(true)}
                    onMouseLeave={() => !isMobile && setCommunityOpen(false)}
                  >
                    <S.NavButton
                      $active={communityOpen}
                      onClick={() => setCommunityOpen((current) => !current)}
                      aria-expanded={communityOpen}
                    >
                      <S.NavIcon $active={communityOpen}>{communityItem.icon}</S.NavIcon>
                      <S.NavLabel $active={communityOpen}>{communityItem.label}</S.NavLabel>
                    </S.NavButton>

                    <S.CommunityDropdown $open={communityOpen}>
                      {communityItem.subItems?.map((subItem) => (
                        <S.CommunityLink
                          key={subItem.id}
                          href={subItem.href}
                          target="_blank"
                          rel="noreferrer"
                          onClick={() => setCommunityOpen(false)}
                        >
                          <S.CommunityIcon>{subItem.icon}</S.CommunityIcon>
                          <span>{subItem.label}</span>
                        </S.CommunityLink>
                      ))}
                      <S.CommunityTip />
                    </S.CommunityDropdown>
                  </S.CommunityWrap>
                )}
              </S.CenterNav>
            </S.CenterNavWrap>

            <S.RightSpacer />
          </S.NavItems>
        )}
      </S.BottomNav>
    </S.Root>
  );
});
